/**
 * Variants d'animation partagés (framer-motion).
 *
 * Import :
 *   import { fadeUpVariants, staggerContainerVariants, staggerItemVariants } from "lib/animations";
 */
import type { Variants } from "framer-motion";

// ─── Easing ───────────────────────────────────────────────────────────────────

/** Cubic-bezier expo-out : très rapide puis très doux */
export const EASE_OUT_EXPO = [0.19, 1, 0.22, 1] as const;

// ─── Fade Up ──────────────────────────────────────────────────────────────────

export const fadeUpVariants: Variants = {
  hidden: { opacity: 0, y: 44 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.85, ease: EASE_OUT_EXPO },
  },
  exit: { opacity: 0, y: 16, transition: { duration: 0.3, ease: "easeIn" } },
};

// ─── Fade In (sans déplacement vertical) ─────────────────────────────────────

export const fadeInVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.55 } },
  exit: { opacity: 0, transition: { duration: 0.25 } },
};

// ─── Scale In ─────────────────────────────────────────────────────────────────

export const scaleInVariants: Variants = {
  hidden: { opacity: 0, scale: 0.93 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.65, ease: EASE_OUT_EXPO },
  },
  exit: {
    opacity: 0,
    scale: 0.96,
    transition: { duration: 0.25 },
  },
};

// ─── Stagger container ────────────────────────────────────────────────────────

/** Wrapper : déclenche l'animation en cascade sur ses enfants */
export const staggerContainerVariants: Variants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1, delayChildren: 0.1 },
  },
};

/** Élément enfant dans un container stagger */
export const staggerItemVariants: Variants = {
  hidden: { opacity: 0, y: 32 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: EASE_OUT_EXPO },
  },
};

// ─── Slide X (carousel / tabs) ────────────────────────────────────────────────

/**
 * Direction : `custom` = 1 (gauche→droite) ou -1 (droite→gauche)
 * @example
 *   <motion.div variants={slideXVariants} custom={direction} initial="enter" animate="center" exit="exit" />
 */
export const slideXVariants: Variants = {
  enter: (dir: number = 1) => ({
    opacity: 0,
    x: dir > 0 ? 64 : -64,
  }),
  center: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.5, ease: EASE_OUT_EXPO },
  },
  exit: (dir: number = 1) => ({
    opacity: 0,
    x: dir > 0 ? -64 : 64,
    transition: { duration: 0.3, ease: "easeIn" },
  }),
};

// ─── Presets whileHover / whileTap ────────────────────────────────────────────

/** Légère élévation au survol d'une carte */
export const cardHoverProps = {
  whileHover: { y: -4, transition: { duration: 0.2 } },
  whileTap: { scale: 0.98, transition: { duration: 0.12 } },
};

/** Micro-retour tactile sur un bouton */
export const buttonTapProps = {
  whileTap: { scale: 0.97, transition: { duration: 0.1 } },
};
